// ─────────────────────────────────────────────────────────────────────────────
// deltaDeck.ts — WHAT A BOOKING TRIGGERS: the company's real consultation deck.
//
// Owner ruling 2026-09-29. The booked list rides the board (`consultation_bookings`,
// composed by `core/delta_board.bookings`); the build is the owner's press:
//   POST /api/delta/deck/build    {company_id}                → the child starts
//   GET  /api/delta/deck/status?company_id=                   → the build's own state
//   POST /api/delta/deck/approve  {company_id, deck_version}  → draft → approved
//   GET  /api/delta/deck/file?company_id=                     → the .pptx
// Same rules as `deltaNeedsRun`: never invent a value · a failed read is UNREAD, not
// empty · a refusal is an answer and its sentence reaches the screen.
// ─────────────────────────────────────────────────────────────────────────────

import { apiBase, authToken } from './apiBase';

export const DECK_BUILD_PATH = '/delta/deck/build';
export const DECK_STATUS_PATH = '/delta/deck/status';
export const DECK_APPROVE_PATH = '/delta/deck/approve';
export const DECK_FILE_PATH = '/delta/deck/file';

/** The STORE'S word for a booking's deck — what is filed, never what is running. */
export type DeckState = 'none' | 'draft' | 'approved';

export interface Booking {
  companyId: string;
  company: string;
  bookedFor: string;
  deck: DeckState;
  deckVersion: string | null;
}

export type BookingsPhase = 'unread' | 'read';

/** The board slot, or UNREAD. A row without a company or a booked time is not a booking. */
export function normalizeBookings(slot: any): { phase: BookingsPhase; rows: Booking[] } {
  if (!slot || !Array.isArray(slot.rows)) return { phase: 'unread', rows: [] };
  const rows = slot.rows
    .filter((r: any) => r && typeof r.company_id === 'string' && r.company_id
      && typeof r.booked_for === 'string' && r.booked_for)
    .map((r: any): Booking => ({
      companyId: r.company_id,
      company: typeof r.Company === 'string' && r.Company ? r.Company : r.company_id,
      bookedFor: r.booked_for,
      deck: r.deck === 'draft' || r.deck === 'approved' ? r.deck : 'none',
      deckVersion: typeof r.deck_version === 'string' && r.deck_version ? r.deck_version : null,
    }));
  return { phase: 'read', rows };
}

/** The build's own state. `null` = never built on this box (or unread). */
export type BuildState = 'building' | 'draft' | 'failed' | 'refused' | 'error';

export interface BuildStatus {
  state: BuildState;
  version: number | null;
  why: string | null;
  failedBars: string[];
}

export function normalizeStatus(body: any): BuildStatus | null {
  const st = body && body.success === true ? body.status : null;
  if (!st || typeof st.state !== 'string') return null;
  const known: BuildState[] = ['building', 'draft', 'failed', 'refused', 'error'];
  if (!known.includes(st.state)) return null;
  return {
    state: st.state,
    version: typeof st.version === 'number' ? st.version : null,
    why: typeof st.why === 'string' && st.why ? st.why : null,
    failedBars: Array.isArray(st.failed_bars) ? st.failed_bars.filter((b: any) => typeof b === 'string') : [],
  };
}

async function send(path: string, body: object): Promise<{ ok: boolean; refused: string | null; body: any }> {
  let res: Response;
  try {
    res = await fetch(apiBase() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authToken() },
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, refused: 'not sent', body: null };
  }
  let out: any = null;
  try { out = await res.json(); } catch { out = null; }
  if (res.status === 404 && !(out && out.error)) return { ok: false, refused: 'not in this build', body: out };
  if (!res.ok || !out || out.success !== true) {
    return { ok: false, refused: (out && typeof out.error === 'string' && out.error) || 'refused', body: out };
  }
  return { ok: true, refused: null, body: out };
}

export const buildDeck = (companyId: string) => send(DECK_BUILD_PATH, { company_id: companyId });

export const approveDeck = (companyId: string, deckVersion: string) =>
  send(DECK_APPROVE_PATH, { company_id: companyId, deck_version: deckVersion });

export async function readBuildStatus(companyId: string): Promise<BuildStatus | null> {
  try {
    const res = await fetch(apiBase() + DECK_STATUS_PATH + '?company_id=' + encodeURIComponent(companyId), {
      headers: { Authorization: 'Bearer ' + authToken() },
    });
    if (!res.ok) return null;
    return normalizeStatus(await res.json());
  } catch {
    return null;
  }
}

/** The file, fetched with the bearer and handed to the browser as a download. */
export async function openDeck(companyId: string, filename: string): Promise<string | null> {
  try {
    const res = await fetch(apiBase() + DECK_FILE_PATH + '?company_id=' + encodeURIComponent(companyId), {
      headers: { Authorization: 'Bearer ' + authToken() },
    });
    if (!res.ok) return 'no deck on file';
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    return null;
  } catch {
    return 'not opened';
  }
}
